import { useEffect, useRef, useState } from "react";
import { Headphones, Pause, Square } from "lucide-react";

/**
 * Bouton "Écouter" — lecture vocale de la page avec surlignage synchronisé
 * de la phrase en cours. Conçu pour les lecteurs dyslexiques :
 * - Position fixe constante en bas à droite
 * - Surligne la phrase lue (bg primary/20) pour faciliter le suivi visuel
 * - Voix française (fr-FR) via l'API SpeechSynthesis native (gratuit, offline)
 *
 * Stratégie de surlignage :
 * 1. Au démarrage, on parcourt <main> (ou <body>) et on enveloppe chaque phrase
 *    dans un <span data-tts-sentence> sans modifier la mise en page.
 * 2. Pour chaque phrase, on crée une utterance ; onstart → on ajoute la classe
 *    de surlignage + scrollIntoView ; onend → on retire la classe.
 * 3. À l'arrêt / fin / démontage, on restaure le DOM original.
 */

const HIGHLIGHT_CLASS = "tts-active-sentence";

const splitIntoSentences = (text: string): string[] => {
  // Découpe en phrases en gardant la ponctuation finale.
  const matches = text.match(/[^.!?…]+[.!?…]+["»)\]]*|\S+$/g);
  return matches ? matches.map((s) => s.trim()).filter(Boolean) : [];
};

interface WrappedSentence {
  span: HTMLSpanElement;
  text: string;
}

const wrapSentencesIn = (root: HTMLElement): WrappedSentence[] => {
  const wrapped: WrappedSentence[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
      const parent = node.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      const tag = parent.tagName;
      if (
        tag === "SCRIPT" ||
        tag === "STYLE" ||
        tag === "NOSCRIPT" ||
        tag === "BUTTON" ||
        parent.closest("[data-tts-skip]") ||
        parent.closest("nav") ||
        parent.closest("footer") ||
        parent.closest("[aria-hidden='true']")
      ) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  const textNodes: Text[] = [];
  let n: Node | null;
  while ((n = walker.nextNode())) textNodes.push(n as Text);

  for (const textNode of textNodes) {
    const raw = textNode.nodeValue || "";
    const sentences = splitIntoSentences(raw);
    if (sentences.length === 0) continue;

    const frag = document.createDocumentFragment();
    let cursor = 0;
    for (const sentence of sentences) {
      const idx = raw.indexOf(sentence, cursor);
      if (idx === -1) continue;
      if (idx > cursor) frag.appendChild(document.createTextNode(raw.slice(cursor, idx)));
      const span = document.createElement("span");
      span.setAttribute("data-tts-sentence", "");
      span.textContent = sentence;
      frag.appendChild(span);
      wrapped.push({ span, text: sentence });
      cursor = idx + sentence.length;
    }
    if (cursor < raw.length) frag.appendChild(document.createTextNode(raw.slice(cursor)));
    textNode.parentNode?.replaceChild(frag, textNode);
  }
  return wrapped;
};

const unwrapAll = () => {
  document.querySelectorAll("[data-tts-sentence]").forEach((el) => {
    const parent = el.parentNode;
    if (!parent) return;
    parent.replaceChild(document.createTextNode(el.textContent || ""), el);
    parent.normalize();
  });
};

const pickFrenchVoice = (): SpeechSynthesisVoice | null => {
  const voices = window.speechSynthesis.getVoices();
  const fr = voices.filter((v) => v.lang?.toLowerCase().startsWith("fr"));
  if (!fr.length) return null;
  const preferred = ["amélie", "amelie", "audrey", "thomas", "google français", "google french"];
  for (const name of preferred) {
    const m = fr.find((v) => v.name.toLowerCase().includes(name));
    if (m) return m;
  }
  return fr[0];
};

export const SpeechifyListenIcon = () => {
  const [state, setState] = useState<"idle" | "playing">("idle");
  const sentencesRef = useRef<WrappedSentence[]>([]);
  const indexRef = useRef(0);
  const stoppedRef = useRef(false);

  const stop = () => {
    stoppedRef.current = true;
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    document.querySelectorAll(`.${HIGHLIGHT_CLASS}`).forEach((el) => el.classList.remove(HIGHLIGHT_CLASS));
    unwrapAll();
    sentencesRef.current = [];
    indexRef.current = 0;
    setState("idle");
  };

  useEffect(() => () => stop(), []);

  const speakNext = () => {
    if (stoppedRef.current) return;
    const i = indexRef.current;
    const items = sentencesRef.current;
    if (i >= items.length) {
      stop();
      return;
    }
    const { span, text } = items[i];
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "fr-FR";
    u.rate = 0.95;
    u.pitch = 1;
    const v = pickFrenchVoice();
    if (v) u.voice = v;
    u.onstart = () => {
      span.classList.add(HIGHLIGHT_CLASS);
      span.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    u.onend = () => {
      span.classList.remove(HIGHLIGHT_CLASS);
      indexRef.current += 1;
      speakNext();
    };
    u.onerror = () => {
      span.classList.remove(HIGHLIGHT_CLASS);
      indexRef.current += 1;
      speakNext();
    };
    window.speechSynthesis.speak(u);
  };

  const start = () => {
    if (!("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    stoppedRef.current = false;
    const root = (document.querySelector("main") as HTMLElement) || document.body;
    const wrapped = wrapSentencesIn(root);
    if (!wrapped.length) return;
    sentencesRef.current = wrapped;
    indexRef.current = 0;
    setState("playing");
    speakNext();
  };

  const handleClick = () => (state === "playing" ? stop() : start());

  return (
    <>
      <style>{`
        .${HIGHLIGHT_CLASS} {
          background-color: hsl(var(--primary) / 0.22);
          color: hsl(var(--foreground));
          border-radius: 0.25rem;
          padding: 0 0.15em;
          box-shadow: 0 0 0 2px hsl(var(--primary) / 0.35);
          transition: background-color 120ms ease;
        }
      `}</style>
      <button
        onClick={handleClick}
        data-tts-skip
        aria-label={state === "playing" ? "Arrêter la lecture" : "Écouter l'intégralité de cette page"}
        title={state === "playing" ? "Arrêter" : "Écouter avec surlignage"}
        className="fixed bottom-5 right-5 z-[60] flex items-center gap-2 px-4 py-3 min-h-[48px] rounded-full bg-primary text-primary-foreground shadow-xl hover:shadow-2xl hover:scale-[1.03] active:scale-95 transition-all border-2 border-primary-foreground/20 font-semibold text-sm"
      >
        {state === "playing" ? (
          <>
            <Square className="w-5 h-5 shrink-0" aria-hidden="true" />
            <span className="hidden sm:inline">Arrêter</span>
          </>
        ) : (
          <>
            <Headphones className="w-5 h-5 shrink-0" aria-hidden="true" />
            <span className="hidden sm:inline">Écouter</span>
          </>
        )}
      </button>
    </>
  );
};

export default SpeechifyListenIcon;
